// Global Authentication State Manager
// This provides a reliable, centralized way to check authentication status
(function() {
    'use strict';
    
    // Create a global auth namespace
    window.GlobalAuth = window.GlobalAuth || {};
    
    // Authentication state
    let authState = {
        isAuthenticated: false,
        userId: null,
        email: null,
        displayName: null,
        firebaseUid: null,
        lastChecked: null
    };
    
    // Event listeners for auth state changes
    const authListeners = [];
    
    // Check authentication status from backend
    async function checkAuthStatus() {
        try {
            const response = await fetch('/api/subscription-status');
            if (response.ok) {
                const data = await response.json();
                if (data.userId) {
                    authState.isAuthenticated = true;
                    authState.userId = data.userId;
                    authState.lastChecked = Date.now();
                    console.log('[GlobalAuth] User authenticated:', authState.userId);
                    notifyListeners();
                    return true;
                }
            }
            authState.isAuthenticated = false;
            authState.userId = null;
            authState.lastChecked = Date.now();
            console.log('[GlobalAuth] User not authenticated');
            notifyListeners();
            return false;
        } catch (error) {
            console.error('[GlobalAuth] Error checking auth status:', error);
            return false;
        }
    }
    
    // Set authentication from Firebase
    function setFirebaseAuth(user) {
        if (user) {
            authState.isAuthenticated = true;
            authState.firebaseUid = user.uid;
            authState.email = user.email;
            authState.displayName = user.displayName;
            authState.lastChecked = Date.now();
            console.log('[GlobalAuth] Firebase auth set:', user.uid);
            
            // Sync with backend
            syncWithBackend(user);
        } else {
            authState.isAuthenticated = false;
            authState.firebaseUid = null;
            authState.email = null;
            authState.displayName = null;
            authState.lastChecked = Date.now();
            console.log('[GlobalAuth] Firebase auth cleared');
        }
        notifyListeners();
    }
    
    // Sync Firebase user with backend
    async function syncWithBackend(user) {
        try {
            // Get the provider ID (google.com, apple.com, or password)
            const providerId = user.providerData && user.providerData.length > 0 
                ? user.providerData[0].providerId 
                : 'password';
            
            // Get the Firebase ID token for server-side verification
            let idToken = null;
            try {
                idToken = await user.getIdToken();
            } catch (tokenError) {
                console.warn('[GlobalAuth] Could not get ID token:', tokenError);
            }
            
            const response = await fetch('/api/auth/sync-user', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                    emailVerified: user.emailVerified,
                    providerId: providerId,
                    idToken: idToken  // Send ID token for server-side verification
                })
            });
            
            if (response.ok) {
                const data = await response.json();
                authState.userId = data.userId;
                authState.emailVerified = user.emailVerified;
                console.log('[GlobalAuth] Synced with backend, userId:', data.userId);
                notifyListeners();
                return true;
            } else if (response.status === 403) {
                // Handle email verification required
                const errorData = await response.json();
                if (errorData.code === 'EMAIL_NOT_VERIFIED') {
                    console.warn('[GlobalAuth] Email not verified:', user.email);
                    const userEmail = user.email;
                    
                    // Check if user just signed up - if so, don't sign them out
                    // Let them stay signed in and see "Free Questions" prompt
                    // Check both window flag and sessionStorage (for page reloads)
                    const justSignedUpFlag = window.justSignedUp || sessionStorage.getItem('justSignedUp') === 'true';
                    if (justSignedUpFlag) {
                        console.log('[GlobalAuth] User just signed up - keeping them signed in for Free Questions flow');
                        authState.isAuthenticated = true;
                        authState.emailVerified = false;
                        authState.verificationRequired = true;
                        notifyListeners();
                        return true; // Return success - user stays signed in
                    }
                    
                    // Sign out the unverified user immediately (only for returning users logging in)
                    try {
                        if (typeof firebase !== 'undefined' && firebase.auth) {
                            await firebase.auth().signOut();
                            console.log('[GlobalAuth] Signed out unverified user:', userEmail);
                        }
                    } catch (signOutError) {
                        console.error('[GlobalAuth] Error signing out unverified user:', signOutError);
                    }
                    
                    authState.isAuthenticated = false;
                    authState.emailVerified = false;
                    authState.verificationRequired = true;
                    authState.firebaseUid = null;
                    authState.userId = null;
                    
                    // Dispatch event for email verification required
                    window.dispatchEvent(new CustomEvent('firebase-auth-verification-required', {
                        detail: {
                            email: userEmail,
                            message: errorData.message
                        }
                    }));
                    
                    notifyListeners();
                    return false;
                }
                console.error('[GlobalAuth] Sync rejected:', errorData);
                return false;
            } else {
                console.error('[GlobalAuth] Failed to sync with backend');
                return false;
            }
        } catch (error) {
            console.error('[GlobalAuth] Error syncing with backend:', error);
            return false;
        }
    }
    
    // Notify all listeners of auth state change
    function notifyListeners() {
        authListeners.forEach(listener => {
            try {
                listener(authState);
            } catch (error) {
                console.error('[GlobalAuth] Error in auth listener:', error);
            }
        });
    }
    
    // Public API
    window.GlobalAuth = {
        // Get current auth state
        getState: function() {
            return { ...authState };
        },
        
        // Check if user is authenticated
        isAuthenticated: function() {
            return authState.isAuthenticated;
        },
        
        // Get user ID
        getUserId: function() {
            return authState.userId;
        },
        
        // Get Firebase UID
        getFirebaseUid: function() {
            return authState.firebaseUid;
        },
        
        // Force check authentication status
        checkAuth: checkAuthStatus,
        
        // Set Firebase authentication (called by Firebase auth handler)
        setFirebaseAuth: setFirebaseAuth,
        
        // Add listener for auth state changes
        onAuthStateChange: function(callback) {
            if (typeof callback === 'function') {
                authListeners.push(callback);
            }
        },
        
        // Remove listener
        offAuthStateChange: function(callback) {
            const index = authListeners.indexOf(callback);
            if (index > -1) {
                authListeners.splice(index, 1);
            }
        },
        
        // Sync current Firebase user with backend
        syncCurrentUser: async function() {
            // Check if we already have backend auth
            if (authState.userId) {
                console.log('[GlobalAuth] Already have backend userId:', authState.userId);
                return true;
            }
            
            // Try to get Firebase user and sync
            if (typeof firebase !== 'undefined' && firebase.auth) {
                const user = firebase.auth().currentUser;
                if (user) {
                    return await syncWithBackend(user);
                }
            }
            
            // Fallback to checking backend directly
            return await checkAuthStatus();
        }
    };
    
    // Initialize by checking auth status
    checkAuthStatus();
    
    // Listen for Firebase auth changes if Firebase becomes available
    function setupFirebaseListener() {
        // Check if Firebase is available from the app.js bundle
        if ((typeof firebase !== 'undefined' && firebase.auth) || 
            (typeof window.firebase !== 'undefined' && window.firebase && window.firebase.auth)) {
            
            const firebaseAuth = window.firebase || firebase;
            console.log('[GlobalAuth] Setting up Firebase auth listener');
            
            firebaseAuth.auth().onAuthStateChanged(function(user) {
                console.log('[GlobalAuth] Firebase auth state changed:', user ? user.uid : 'null');
                setFirebaseAuth(user);
            });
            
            // Also check current user immediately
            const currentUser = firebaseAuth.auth().currentUser;
            if (currentUser) {
                console.log('[GlobalAuth] Found current Firebase user:', currentUser.uid);
                setFirebaseAuth(currentUser);
            }
        } else {
            // Retry in a second
            setTimeout(setupFirebaseListener, 1000);
        }
    }
    
    // Start trying to set up Firebase listener
    setupFirebaseListener();
    
    // Also expose a method to manually check Firebase
    window.GlobalAuth.checkFirebase = function() {
        if ((typeof firebase !== 'undefined' && firebase.auth) || 
            (typeof window.firebase !== 'undefined' && window.firebase && window.firebase.auth)) {
            
            const firebaseAuth = window.firebase || firebase;
            const currentUser = firebaseAuth.auth().currentUser;
            if (currentUser) {
                console.log('[GlobalAuth] Manual check found user:', currentUser.uid);
                setFirebaseAuth(currentUser);
                return true;
            }
        }
        return false;
    };
    
    console.log('[GlobalAuth] Global authentication system initialized');
})();